import { LOCALE_PREFIX_RE } from "@/i18n/routing";

/**
 * Routes where the docked mini assistant is MOBILE-ONLY (owner, 2026-09-07).
 *
 * The bar became desktop-visible earlier the same day, on the reasoning that
 * once a reader scrolls past the assistant hero the docked pill is the only
 * thing still carrying the assistant. That holds on browsing pages. It does not
 * hold on the two surfaces where the reader has already committed to a
 * different way of finding someone, and where the desktop layout gives that way
 * its own persistent furniture:
 *
 *   /search              — the filter sidebar IS the search affordance, and it
 *                          stays on screen the whole way down.
 *   /professionals/<slug> — the profile's own contact card is the action, and
 *                          a floating "ask us instead" pill competes with it.
 *
 * Below `lg` neither of those is persistent (the sidebar collapses behind the
 * Filters button, the contact card scrolls away), so the bar still earns its
 * place there — which is why this suppresses a BREAKPOINT and not the bar.
 *
 * ⚠️ Distinct from the `[data-hero-search]` reveal gate, which hides the bar
 * whenever a hero search/assistant input is on screen at ANY width. That one is
 * about two inputs competing in the same viewport; this one is about the bar
 * competing with a whole desktop layout. Neither implies the other.
 */
const DESKTOP_SUPPRESSED: readonly RegExp[] = [
  /^\/search(?:\/|$)/,
  /^\/professionals\/[^/]+/,
];

/**
 * Should the docked bar be hidden from `lg` up on this path?
 *
 * Pure, and locale-stripped first: `usePathname()` returns the URL as routed,
 * so a Vietnamese reader is on `/vi/search`. Matching the raw path would have
 * left the bar showing on every non-default locale — a silent half-fix, and
 * exactly the kind that reads as "works on my machine" because the default
 * locale is unprefixed.
 */
export function miniChatDesktopSuppressed(pathname: string | null | undefined): boolean {
  const p = (pathname || "/").replace(LOCALE_PREFIX_RE, "") || "/";
  return DESKTOP_SUPPRESSED.some((re) => re.test(p));
}

/**
 * Routes where the docked MINI SEARCH — the fallback variant, `mini-search-fallback-spec.md`
 * §7 — is suppressed at EVERY width (owner decision D1, 2026-09-08).
 *
 * `/search` only, and the reasoning is not the one above. The rule above
 * suppresses a BREAKPOINT because the desktop layout gives the reader its own
 * persistent way in; this one suppresses the CONTROL, because a docked search
 * box on the search results page is offering the reader the page they are
 * already standing on. That is true at 375px as much as at 1440px.
 *
 * ⚠️ A professional profile is deliberately NOT here. The `lg`+ rule above
 * still covers it (the contact card is the action, and a floating pill competes
 * with it), but below `lg` that card scrolls away and a way to search for
 * someone else still earns its place.
 */
const SEARCH_SUPPRESSED: readonly RegExp[] = [/^\/search(?:\/|$)/];

/**
 * Should the docked mini SEARCH be hidden entirely on this path?
 *
 * Locale-stripped first, for the reason spelled out on `miniChatDesktopSuppressed`:
 * `usePathname()` returns the path as routed, so a Vietnamese reader is on
 * `/vi/search`, and the default locale being unprefixed is what hides a raw-path
 * match from anyone testing in English.
 */
export function miniSearchSuppressed(pathname: string | null | undefined): boolean {
  const p = (pathname || "/").replace(LOCALE_PREFIX_RE, "") || "/";
  return SEARCH_SUPPRESSED.some((re) => re.test(p));
}
